import React, {useEffect, useRef, useState} from 'react';
import {Avatar, Button, Col, Layout, Popover, Row, Select, Skeleton, Space, Tooltip} from 'antd';
import InputPage from '../components/InputPage';
import TrajectoryChart from "../components/TrajectoryChart";
import {ChartLoader} from "../components/util/ChartLoader";
import {useGoogleLogin} from "react-use-googlelogin";
import 'react-toastify/dist/ReactToastify.css';
import {useDispatch, useSelector} from "react-redux";
import {setTrajectoryStore} from "../store/entities/trajectory";
import $ from "jquery";
import {useParams} from "react-router-dom";
import CookieConsent from "react-cookie-consent";
import {toast} from "react-toastify";
// import {captureScreenshot} from "../components/util/ScreenshotCreator";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faKeyboard} from "@fortawesome/free-solid-svg-icons";
import {SettingsModal} from "../components/SettingsModal";
import {parse} from 'wellknown';
import {faLink} from "@fortawesome/free-solid-svg-icons/faLink";

const clientId = "607020806390-1lvs93fvcei6ntf06gcsdsbrl415t5v9.apps.googleusercontent.com"
const {Header} = Layout;
const {Option} = Select;

let dashboardsBackendUrl;
if (window.location.href.match('trajectory-heatmap-dev-dot')) {
  dashboardsBackendUrl = 'https://dashboards-backend-dev-ootgoxvrsq-uc.a.run.app/';
} else if (window.location.href.match('trajectory-heatmap-dot')) {
  dashboardsBackendUrl = 'https://dashboards-backend-ootgoxvrsq-uc.a.run.app/';
} else {
  dashboardsBackendUrl = 'https://dashboards-backend-dev-ootgoxvrsq-uc.a.run.app/';
}

let authRetryCount = 0;

const TrajectoryApp = props => {
  const trajectoryChartRef = useRef();
  let params = useParams();
  const dispatch = useDispatch();

  // Grab stuff from the store
  const organizationName = useSelector(state => state.entities.trajectory.organizationName);
  const userEmail = useSelector(state => state.entities.trajectory.userEmail);

  // Setup the components (local) state
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarPopoverVisible, setAvatarPopoverVisible] = useState(false);
  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(localStorage.getItem('preferredOrganization'));
  const [heatmapId, setHeatmapId] = useState(params.heatmapId);

  // Initiate the Google login module
  // This is a great example of object destructuring as well as object literals (clientId) (both of which are fun ES6 tricks)
  const {signIn, signOut, googleUser, isSignedIn, isInitialized} = useGoogleLogin({
    clientId,
    persist: true,
    cookiePolicy: 'none'
  })

  // On response from Google, authenticate with the backend and get a session ID
  const handleSignIn = async () => await signIn();
  const submitListener = () => trajectoryChartRef.current.createPlot()

  // Make the title 'DEV ...' if it is
  let href = window.location.href;
  if (href.match('dev') || !href.match('appspot')) document.title = 'DEV Trajectory Heatmap'

  // If the react-use-googlelogin thing is loaded and ready to go, hit backend/authenticate/org_name
  // By waiting for isInitialized, we wait to send an auth request until we know if the user is still signed in or not.
  useEffect(() => isInitialized && authenticate(), [isSignedIn, isInitialized, selectedOrganization])

  // All this does is show a success toast. Nothing exciting is done here.
  useEffect(() => {
    if (organizationName && organizationName !== 'Default') {
      toast.success(`Logged into ${organizationName}!`)
    }
  }, [organizationName])

  // Send the auth request
  const authenticate = () => {
    /**
     * With current behavior, it will chose the organization in which the user has the highest privilege,
     * Only request login to specific organization if they've specified, otherwise, let the backend chose
     * and chose alphabetically from there
     */
    $.ajax(`${dashboardsBackendUrl}authenticate/${selectedOrganization || ''}`, {
      method: 'GET',
      beforeSend: xhr => {
        if (isSignedIn) {
          xhr.setRequestHeader('Authorization', 'Bearer ' + googleUser.tokenId)
        }
      },
      success: data => {
        // If they aren't logged into the default org, set their selected org
        if (data.organization_name !== 'Default') setSelectedOrganization(data.organization_name);

        let parsedCenter = parse(data.map_center)
        let mapCenter = {lat: parsedCenter.coordinates[1], lng: parsedCenter.coordinates[0]}
        sessionStorage.setItem('sessionId', data.session_id)
        // Set the session ID, role, and org name, in addition to email if it exists.
        dispatch(setTrajectoryStore({
          sessionId: data.session_id,
          role: data.role,
          organizationName: data.organization_name,
          mapCenter,
          mapZoom: data.map_zoom,
          /**
           * ..(condition && {objKey: objValue}) will only include objKey in the object IF condition is true.
           * ... is the spread operator. https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Spread_syntax
           * && conditionally invokes short-circuit evaluation.
           * This combo only asks the spread operator to spread {obj: objValue} IF the first condition evaluates to true.
           * You can do this with arrays too! https://amberley.dev/blog/2020-09-07-conditionally-add-to-array-or-obj/
           */
          ...((!!googleUser && !!googleUser.profileObj) && {userEmail: googleUser.profileObj.email})
        }))

        // The reason we have to pass the session ID is because the store will take a bit to reflect the changes, and I don't want to wait.
        // Pull the user's available organizations so they can switch
        getAvailableOrganizations(data.session_id);
        // Load the heatmap
        loadHeatmap(data.session_id);
      },
      error: err => {
        // The user doesn't belong to the organization
        if (err.status === 403 && err.responseText === "User doesn't belong to that organization!") {
          // Reset the organization
          setSelectedOrganization('');
          localStorage.removeItem('preferredOrganization')
          // Make the request again
          // By doing ++ at the end, it will return the current value of authRetryCount, then increment the variable
          if (authRetryCount++ < 1) authenticate();
        }
        // Debug
        console.log(err)
      }
    })
  }

  /**
   * Get the available organizations for a user. Used to populate the dropdown.
   * Calls setAvailable organizations
   * @param sessionId The session ID to use
   */
  const getAvailableOrganizations = sessionId => {
    // If they have a sessionId and an email
    // How I'd like to do this is:
    //      'profileObj' in googleUser
    // However, if googleUser is null/undefined, it throws an error b/c 'in' expects an object.
    if (sessionId && googleUser && googleUser.profileObj) {
      $.ajax(`${dashboardsBackendUrl}user/organization/${googleUser.profileObj.email}`, {
          method: 'GET',
          beforeSend: xhr => xhr.setRequestHeader('Authorization', `Bearer ${sessionId}`),
          success: setAvailableOrganizations,
          error: err => toast.error("Error getting organizations!")
        }
      );
    }
  }

  /**
   * GET the info about the heatmap identified by heatmapId
   * Updates the store after getting the info
   * @param sessionId The session ID to use
   */
  const loadHeatmap = sessionId => {
    if (heatmapId && sessionId) {
      // Make a request for the heatmap and include the UTC offset
      // The offset is used to process relative dates
      $.ajax(`${dashboardsBackendUrl}trajectory_heatmap/${heatmapId}/${new Date().getTimezoneOffset()}`, {
        method: 'GET',
        beforeSend: xhr => xhr.setRequestHeader('Authorization', `Bearer ${sessionId}`),
        success: data => {
          // If there is data, then just set data to the first element
          if (data.length > 0) data = data[0];

          // Setup the Redux store
          // You could do something like this, but it's either a lot of code, or not worth it for the (heavy) lodash
          // https://stackoverflow.com/questions/12931828/convert-returned-json-object-properties-to-lower-first-camelcase
          dispatch(setTrajectoryStore({
            startDate: data.start_date,
            endDate: data.end_date,
            dateRangeId: data.date_range_id,
            routeId: data.route_id,
            routeName: data.route_name,
            roadWidth: data.road_width,
            mileMarkerOffset: data.mile_marker_offset,
            subtractMileMarkers: data.subtract_mile_markers,
            flipMileMarkers: data.flip_mile_markers,
            includeTrucks: data.include_trucks,
            maxTravelTime: data.max_travel_time,
            waypoints: data.route_waypoints,
            eventTable: data.event_table,
            movementTable: data.movement_table,
            truckTable: data.truck_table,
            submitFromURL: true,
            loadedMaxTravelTime: !!data.max_travel_time,
            loadedNewHeatmap: true,
            heatmapId
          }));
        },
        error: err => {
          toast.error("Heatmap doesn't exist. Please check the URL.");
          console.log(err)
        }
      })
    }
  }

  const useDarkMode = (localStorage.getItem('darkMode') === 'true')

  return (
    <Layout className="layout" style={{backgroundColor: useDarkMode ? '#000' : '#fff'}}>
      <Header style={{paddingLeft: '16px', paddingRight: '8px'}}>
        <div style={{display: 'flex', alignItems: 'center', height: '64px'}}>
          <h1 style={{color: '#fff', flexGrow: 1, fontSize: '2.5em', paddingTop: '8px'}}>Trajectory
            Heatmap</h1>
          <Space>
            {/* Only show this stuff if it's the dev version */}
            {(window.location.href.match('dev') || !window.location.href.match('appspot')) &&
              <Space>
                <h2 style={{color: 'white'}}>Testing version! <a
                  href={'https://trajectory-heatmap-dot-tmc-dashboards.uc.r.appspot.com' + window.location.pathname}>Go
                  to a more stable
                  version</a></h2>
                <Button danger onClick={() => {
                  window.open('https://github.itap.purdue.edu/JTRP/mobility.trajectory_heatmap_frontend/issues', '_blank');
                }}>Report Bug</Button>
              </Space>}

            <Tooltip title="Quick Links">
              <Button icon={<FontAwesomeIcon icon={faLink}/>}
                      href='https://tmc-dashboards.uc.r.appspot.com/trajectory-heatmap-links/'
                      target='_blank'/>
            </Tooltip>

            {/* Keyboard Shortcuts */}
            {/* TODO Add toggle switches for these for mobile users */}
            <Popover content={(
              <div>
                <div>'<b>[</b>', '<b>]</b>' - Travel Time Point Size</div>
                <div>'<b>s</b>' - Show Statistics</div>
                <div>'<b>t</b>' - Toggle Travel Time Plot</div>
                <div>'<b>h</b>' - Toggle Hard Braking Histogram</div>
                <div>'<b>d</b>' - Download Trips</div>
                <div>'<b>p</b>' - Trip Slideshow</div>
              </div>
            )} title="Keyboard Shortcuts">
              <Button icon={<FontAwesomeIcon icon={faKeyboard}/>}/>
            </Popover>

            {/* Screenshot button */}
            {/*<Tooltip title="Capture Screenshot">*/}
            {/*    <Button icon={<FontAwesomeIcon icon={faCamera}/>} shape='square'*/}
            {/*            onClick={captureScreenshot}/>*/}
            {/*</Tooltip>*/}

            {/* Add the SettingsModal, which includes the button */}
            <Tooltip title="Settings">
              <SettingsModal/>
            </Tooltip>

            {!isSignedIn && !isInitialized && (
              <Skeleton.Avatar active style={{marginTop: '6px'}} size={48}/>
            )}

            {/* Only add the sign in button if they aren't signed in */}
            {!isSignedIn && isInitialized && (
              <div>
                <Button onClick={() => handleSignIn()} style={{marginTop: '6px'}}>
                  Sign in
                </Button>
              </div>
            )}

            {/* If the user is signed in, show the avatar with the organization switcher dropdown */}
            {/* TODO Change this to display the user's name. Need to add this to the backend/authenticate response... */}
            {isSignedIn && (
              <div>
                <Popover
                  placement="bottomRight"
                  trigger='click'
                  title={userEmail}
                  visible={avatarPopoverVisible}
                  onVisibleChange={setAvatarPopoverVisible}
                  content={
                    <Space direction='vertical'>
                      <Select
                        style={{width: '200px'}}
                        value={selectedOrganization}
                        onChange={value => {
                          setSelectedOrganization(value)
                          // Remember which org the user wants to login to
                          localStorage.setItem('preferredOrganization', value);
                        }}
                      >
                        {/* Add an option for each available organization */}
                        {availableOrganizations.map(org => (
                          <Option value={org.organization_name} key={org.organization_name}>
                            {`${org.organization_name} | ${org.role_name}`}
                          </Option>
                        ))})
                      </Select>
                      <a href="mailto:desaij@purdue.edu?cc=darcy@purdue.edu&subject=Trajectory%20Heatmap%3A%20New%20User%20Request&body=Please%20fill%20out%20the%20form%20below:%0D%0A%0D%0AGmail%20address%3A%0D%0AFirst%20name%3A%0D%0ALast%20name%3A%0D%0AOrganization%3A%0D%0A%0D%0ANotes%20(optional)%3A%0D%0A%0D%0A">
                        Join your organization
                      </a>

                      <Button danger style={{float: 'right'}} onClick={() => {
                        signOut().then(() => {
                          localStorage.removeItem('preferredOrganization');
                          setSelectedOrganization('');
                          authRetryCount = 0;
                        });
                      }}>Sign Out</Button>
                    </Space>
                  }
                >
                  <Avatar
                    style={{
                      cursor: 'pointer',
                      position: 'relative',
                      right: '0',
                      backgroundColor: '#000',
                      marginBottom: '4px'
                    }}
                    src={googleUser.profileObj.imageUrl}
                    size={48}
                  >
                    {(avatarUrl) ? "" : "Login"}
                  </Avatar>
                </Popover>
              </div>
            )}
          </Space>
        </div>
      </Header>
      <Row>
        <Col span={18}>
          <div id="trajectory-chart-container" className="content-layout-no-border">
            <TrajectoryChart ref={trajectoryChartRef}/>
          </div>
        </Col>
        <Col span={6}>
          <div className="content-layout-no-border">
            <InputPage/>
          </div>
        </Col>
      </Row>
      <CookieConsent>This website uses cookies to keep you logged in.</CookieConsent>
      <ChartLoader submitListener={submitListener}/>
    </Layout>
  );
}

export default TrajectoryApp;
